import { useCallback, useEffect, useRef } from 'react';
import type { RefObject } from 'react';

type EventCallback = (event: any) => void;

/**
 *	Adds an event listener
 *
 *	@param type string Event type
 *	@param callback (event: Event | SyntheticEvent) => void
 *	@param callbackDeps any[] Array with callback dependencies, useful when callback changes
 *	@param targetNodeRef RefObject<T> Target node to listen for events, defaults to window
 */
export function useEventListener<
	EventType,
	T extends HTMLElement = HTMLDivElement,
>(
	type: string,
	callback: EventCallback,
	callbackDeps?: any[],
	targetNodeRef?: RefObject<T | null> | Document
): void {
	const cachedCallback = useRef<EventCallback>(null);

	if (!callbackDeps) {
		callbackDeps = [];
	}

	useEffect(() => {
		let target: T | Document | Window;

		if (targetNodeRef?.hasOwnProperty('current')) {
			target = (targetNodeRef as RefObject<T>).current as T;
		} else if (targetNodeRef instanceof Document) {
			target = targetNodeRef;
		} else {
			target = window;
		}

		if (!(target && target.addEventListener)) return;

		if (cachedCallback.current !== callback) {
			cachedCallback.current = callback;
		}

		const eventListener: EventCallback = (event: EventType) => {
			if (!!cachedCallback?.current) {
				cachedCallback.current(event as Event);
			}
		};

		target.addEventListener(type, eventListener);

		return () => target.removeEventListener(type, eventListener);
	}, [type, targetNodeRef, ...callbackDeps]);
}

type EventDispatcher<T> = (detail?: T) => void;

/**
 *	Creates and return a Custom Event dispatcher.
 *
 *	@param type string Event type e.g "app-greeting"
 *	@param target EventTarget
 *	@returns EventDispatcher
 */
export function useEventDispatcher<T = any>(
	type: string,
	target: EventTarget = window
): EventDispatcher<T> {
	const dispatcher = useCallback(
		(detail?: T) => {
			target.dispatchEvent(new CustomEvent(type, { detail }));
		},
		[type, target]
	);

	return dispatcher;
}
